
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useGetSupplierById, useCreateSupplier, useUpdateSupplier } from '@/hooks/use-supplier';
import { Loader2 } from 'lucide-react';
import SupplierFormComponent from '@/components/suppliers/SupplierForm';
import { Button } from '@/components/ui/button';
import { toast } from "sonner";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import SupplierProducts from '@/components/suppliers/SupplierProducts';

const SupplierForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id && id !== 'new';
  
  const { data: supplier, isLoading, error: fetchError } = useGetSupplierById(
    isEditing ? id : '', 
    { enabled: isEditing }
  );
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();

  const handleSubmit = async (data: any) => {
    try {
      if (isEditing && id) {
        await updateSupplier.mutateAsync({ id, data });
        toast.success("Supplier updated successfully");
      } else {
        await createSupplier.mutateAsync(data);
        toast.success("Supplier created successfully");
      }
      navigate('/suppliers');
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error("Failed to save supplier. Please try again.");
    }
  };

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/suppliers')} className="mb-4">
            Back to Suppliers
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Edit Supplier' : 'Add Supplier'}
          </h1>
        </div>

        {fetchError && isEditing && (
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to fetch supplier details. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{isEditing ? 'Edit Supplier Details' : 'Supplier Information'}</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing && isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <SupplierFormComponent 
                initialData={supplier} 
                onSubmit={handleSubmit} 
                isSubmitting={createSupplier.isPending || updateSupplier.isPending}
              />
            )}
          </CardContent>
        </Card>

        {/* Supplier Products Section - Only show when editing existing supplier */}
        {isEditing && supplier && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Supplier Products</CardTitle>
            </CardHeader>
            <CardContent>
              <SupplierProducts 
                supplierId={supplier.id}
                supplierProducts={supplier.supplier_products || []}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SupplierForm;
